// Self-hosted Cookie Consent Manager (CMP) — Vue side.
//
// Responsibilities here: the banner/preferences UI state, persistence
// (localStorage, ~6-month TTL per the Garante), the Firestore audit log, and
// best-effort cookie cleanup on revocation.
//
// The low-level tag gating (Google Consent Mode v2 defaults + conditional
// injection of gtag.js / GTM) lives in the inline bootstrap in
// public/index.html, exposed as `window.IcConsent`. That bootstrap runs BEFORE
// any Google tag, so nothing non-technical fires until the visitor consents.
// This module is the single source of truth for *recording* a choice and then
// asks the bootstrap to apply it.
//
// Keep STORAGE_KEY / VERSION / MAX_AGE_MS in sync with public/index.html — we
// read them off window.IcConsent when present and only fall back to these.
import { reactive } from 'vue';
import { db, firebase } from '@/firebase/init';

const FALLBACK = {
  STORAGE_KEY: 'ic_cookie_consent',
  VERSION: 1,
  MAX_AGE_MS: 1000 * 60 * 60 * 24 * 182, // ~6 months
};

const CLIENT_ID_KEY = 'ic_cookie_client_id';
const CONSENT_LOG_COLLECTION = 'cookieConsents';
const REOPEN_EVENT = 'ic:open-cookie-preferences';

export const DEFAULT_CATEGORIES = { necessary: true, analytics: false, marketing: false };

function ic() {
  return (typeof window !== 'undefined' && window.IcConsent) || {};
}
function cfg(key) {
  const v = ic()[key];
  return v != null ? v : FALLBACK[key];
}

// Reactive state driving <CookieConsentBanner>.
export const consentState = reactive({
  open: false, // banner or preferences visible
  mode: 'banner', // 'banner' | 'preferences'
  decided: false, // a valid, unexpired choice is stored
});

// Read the stored decision (null if absent / wrong version / older than the TTL).
// Prefer the bootstrap's reader so the expiry + version logic stays in one place.
function readRecord() {
  if (typeof ic().read === 'function') return ic().read();
  try {
    const raw = localStorage.getItem(cfg('STORAGE_KEY'));
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec || rec.version !== cfg('VERSION')) return null;
    if (!rec.timestamp || Date.now() - rec.timestamp > cfg('MAX_AGE_MS')) return null;
    return rec;
  } catch (e) {
    return null;
  }
}

export function currentCategories() {
  const rec = readRecord();
  return rec && rec.categories
    ? { ...DEFAULT_CATEGORIES, ...rec.categories, necessary: true }
    : { ...DEFAULT_CATEGORIES };
}

// Stable pseudonymous id so repeated choices from the same browser can be
// correlated in the audit log. Not a fingerprint; a random value we store.
function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id =
        typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : 'c-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return 'anon';
  }
}

// Best-effort removal of the cookies GA4 / GTM set, used when a previously
// granted category is switched off (revocation should not leave cookies behind).
function clearTrackingCookies() {
  const patterns = [/^_ga/, /^_gid$/, /^_gat/, /^_gcl/, /^_fbp$/, /^_fbc$/];
  const host = window.location.hostname;
  const domains = ['', host, '.' + host];
  const parts = host.split('.');
  if (parts.length > 2) domains.push('.' + parts.slice(-2).join('.'));
  document.cookie.split(';').forEach((chunk) => {
    const name = chunk.split('=')[0].trim();
    if (!name || !patterns.some((p) => p.test(name))) return;
    domains.forEach((d) => {
      document.cookie =
        name +
        '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/' +
        (d ? '; domain=' + d : '');
    });
  });
}

// Fire-and-forget write of the consent record for audit/proof. If the Firestore
// rule for `cookieConsents` isn't deployed yet (or the client is offline), this
// rejects with permission-denied — we swallow it. The choice is already stored
// client-side and the tags are already gated, so the UI must never depend on it.
function logToFirestore(rec) {
  try {
    const payload = {
      method: rec.method,
      categories: {
        necessary: true,
        analytics: !!rec.categories.analytics,
        marketing: !!rec.categories.marketing,
      },
      clientId: getClientId(),
      userAgent: (window.navigator.userAgent || '').slice(0, 300),
      path: (window.location.pathname || '').slice(0, 300),
      policyVersion: cfg('VERSION'),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    };
    const p = db.collection(CONSENT_LOG_COLLECTION).add(payload);
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (e) {
    /* never block the UI on logging */
  }
}

// Persist a choice, apply it (Consent Mode + tag injection), clean up on
// revocation, and record it. `method` is one of accept_all | reject_all | custom.
export function saveConsent(categories, method) {
  const cats = {
    necessary: true,
    analytics: !!categories.analytics,
    marketing: !!categories.marketing,
  };
  const prev = currentCategories();
  const rec = {
    version: cfg('VERSION'),
    timestamp: Date.now(),
    method,
    categories: cats,
  };

  try {
    localStorage.setItem(cfg('STORAGE_KEY'), JSON.stringify(rec));
  } catch (e) {
    /* storage may be unavailable (private mode); tags still gate in-memory */
  }

  if (typeof ic().activate === 'function') ic().activate(cats);

  if ((prev.analytics && !cats.analytics) || (prev.marketing && !cats.marketing)) {
    clearTrackingCookies();
  }

  logToFirestore(rec);

  consentState.decided = true;
  consentState.open = false;
  consentState.mode = 'banner';
}

export function acceptAll() {
  saveConsent({ analytics: true, marketing: true }, 'accept_all');
}
export function rejectAll() {
  saveConsent({ analytics: false, marketing: false }, 'reject_all');
}

export function openPreferences() {
  consentState.mode = 'preferences';
  consentState.open = true;
}

let initialised = false;
// Called once when the app mounts: show the banner on first visit (or after the
// choice expires), and wire the footer "Impostazioni cookie" reopeners.
export function initConsentUi() {
  consentState.decided = !!readRecord();
  if (!consentState.decided) {
    consentState.mode = 'banner';
    consentState.open = true;
  }
  if (!initialised && typeof window !== 'undefined') {
    initialised = true;
    window.addEventListener(REOPEN_EVENT, openPreferences);
  }
}
